import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveCategoryPath, toSlug } from '@/shell/sports-menu-utils';
import { EmptyState } from '@/ui';
import { EventsFeed, EventsFeedSkeleton } from './-components/EventsFeed';

export const Route = createFileRoute('/sports/$sport/$category/')({
  component: CategoryEventsPage,
});

function CategoryEventsPage() {
  const { sport, category } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());

  const resolved = useMemo(
    () => resolveCategoryPath(menuQuery.data, sport, category),
    [menuQuery.data, sport, category],
  );

  const filter = useMemo(
    () =>
      resolved
        ? {
            Sport: resolved.sport.Name,
            Category: resolved.category.Name,
          }
        : null,
    [resolved],
  );

  if (menuQuery.isPending) {
    return <EventsFeedSkeleton />;
  }

  if (!resolved || !filter) {
    return (
      <EmptyState
        icon="flag"
        title={t('CategoryNotFound', { defaultValue: 'Category not found' })}
        description={t('CategoryNotInMenu', {
          defaultValue: 'This category is not in the current sports menu.',
        })}
      />
    );
  }

  return (
    <EventsFeed
      scope="category"
      filter={filter}
      title={translateSportsLabel(i18n, t, resolved.category.Id, resolved.category.Name)}
      subtitle={translateSportsLabel(i18n, t, resolved.sport.Id, resolved.sport.Name)}
      emptyDescription={t('NoEventsForCategory', {
        defaultValue: 'No events available for this category right now.',
      })}
      onOpenEvent={({ eventId, tournament: tournamentName, offer }) => {
        const tournamentSlug = tournamentName ? toSlug(tournamentName) : undefined;
        if (!tournamentSlug) return;
        void navigate({
          to: '/sports/$sport/$category/$tournament/$eventId',
          params: {
            sport,
            category,
            tournament: tournamentSlug,
            eventId: String(eventId),
          },
          search: { offer },
        });
      }}
    />
  );
}
